import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Custom hook for tracking image loading states.
 * Tracks which images have loaded successfully and which have failed,
 * using cache keys to handle image updates.
 *
 * @returns {object} - Image loader utilities
 */
export function useImageLoader() {
  const [errors, setErrors] = useState({});
  const [loaded, setLoaded] = useState({});

  // Refs for use in callbacks that need current values without re-renders
  const errorsRef = useRef({});
  const loadedRef = useRef({});

  useEffect(() => {
    errorsRef.current = errors;
  }, [errors]);

  useEffect(() => {
    loadedRef.current = loaded;
  }, [loaded]);

  /**
   * Mark an image as failed to load
   * @param {string} imageId - The image ID
   * @param {string} cacheKey - The cache key (e.g., updated_at timestamp)
   */
  const onError = useCallback((imageId, cacheKey) => {
    setErrors((prev) => ({ ...prev, [imageId]: cacheKey }));
  }, []);

  /**
   * Mark an image as successfully loaded
   * @param {string} imageId - The image ID
   * @param {string} cacheKey - The cache key
   */
  const onLoad = useCallback((imageId, cacheKey) => {
    // Clear any error for this image
    setErrors((prev) => {
      const next = { ...prev };
      delete next[imageId];
      return next;
    });
    // Mark as loaded
    setLoaded((prev) => ({ ...prev, [imageId]: cacheKey }));
  }, []);

  /**
   * Check if an image has loaded successfully for the current cache key
   * @param {string} imageId - The image ID
   * @param {string} cacheKey - The cache key to check against
   * @returns {boolean}
   */
  const isLoaded = useCallback(
    (imageId, cacheKey) => loaded[imageId] === cacheKey,
    [loaded]
  );

  /**
   * Check if an image has failed to load for the current cache key
   * @param {string} imageId - The image ID
   * @param {string} cacheKey - The cache key to check against
   * @returns {boolean}
   */
  const isBroken = useCallback(
    (imageId, cacheKey) => errors[imageId] === cacheKey,
    [errors]
  );

  /**
   * Check if an image had an error (using ref, for use in callbacks)
   * @param {string} imageId - The image ID
   * @returns {boolean}
   */
  const hadError = useCallback((imageId) => !!errorsRef.current[imageId], []);

  /**
   * Clear all tracking state (useful when photo list changes significantly)
   */
  const reset = useCallback(() => {
    setErrors({});
    setLoaded({});
  }, []);

  return {
    onError,
    onLoad,
    isLoaded,
    isBroken,
    hadError,
    reset,
    // Expose refs for advanced use cases
    errorsRef,
    loadedRef,
  };
}

export default useImageLoader;
